import { Icon } from '@iconify/react';
import type { FC } from 'react';
import { z } from 'zod';

import BulletsTitle from '@/components/BulletPoints/BulletsTitle';
import Button from '@/components/Button';
import Text from '@/components/Text';
import { useQueriesManage } from '@/hooks/useQueriesManage';
import type { CompanyProfileDetail } from '@/services/external-offers';
import {
  CompanyProfileRevenueEnum,
  CompanyProfileSizeEnum,
} from '@/services/external-offers';

import Layout from './Layout';

type Props = {
  data: CompanyProfileDetail;
};
const Overview: FC<Props> = ({ data }) => {
  const { setParams } = useQueriesManage({
    paramsSchema: z.object({ details: z.string().optional() }),
  });

  const companySize = {
    [CompanyProfileSizeEnum.UNKNOWN]: 'Unknown',
    [CompanyProfileSizeEnum['1_50']]: '1 - 50 employees',
    [CompanyProfileSizeEnum['51_200']]: '51 - 200 employees',
    [CompanyProfileSizeEnum['201_500']]: '201 - 500 employees',
    [CompanyProfileSizeEnum['501_1000']]: '501 - 1000 employees',
    [CompanyProfileSizeEnum['1001_5000']]: '1001 - 5000 employees',
    [CompanyProfileSizeEnum['5001_10000']]: '5001 - 10000 employees',
    [CompanyProfileSizeEnum.OVER_10000]: '10k+ employees',
  };
  const companyRevenue = {
    [CompanyProfileRevenueEnum.UNKNOWN]: 'Unknown',
    [CompanyProfileRevenueEnum.UNDER_100K]: 'Under $100k (USD)',
    [CompanyProfileRevenueEnum['100K_1M']]: '$100k - $1M (USD)',
    [CompanyProfileRevenueEnum['1M_10M']]: '$1M - $10M (USD)',
    [CompanyProfileRevenueEnum.OVER_10M]: '$10+ bilion (USD)',
  };
  const viewData = [
    {
      label: 'Website',
      value: data.linkMainSite,
      icon: 'material-symbols-light:language',
    },
    {
      label: 'Founded',
      value: data.founded,
      icon: 'material-symbols-light:cake-outline',
    },
    {
      label: 'Size',
      value: companySize[data.size],
      icon: 'material-symbols-light:finance',
    },
    {
      label: 'Locations',
      value: data.mainLocation,
      icon: 'material-symbols-light:location-on-outline',
    },
    {
      label: 'Revenue',
      value: companyRevenue[data.revenue],
      icon: 'material-symbols-light:savings-outline',
    },
  ];
  const companyName = data.name;

  return (
    <>
      <Layout title={`${companyName} Overview`}>
        <div className="grid grid-cols-1 gap-10 text-neutral-800 md:grid-cols-3">
          {viewData.map(
            ({ label, value, icon }) =>
              value && (
                <DataView
                  key={`${value}-${label}`}
                  label={label}
                  value={value}
                  icon={icon}
                />
              )
          )}
        </div>
      </Layout>
      <Layout title={`${companyName} Bullets`}>
        <BulletsTitle />
        <div className="flex w-full justify-end">
          <Button
            variant="jbGradientBorder"
            onClick={() => setParams({ details: 'Bullets' })}
          >
            See Bullets for {companyName}
          </Button>
        </div>
      </Layout>
    </>
  );
};

export default Overview;
const DataView = ({
  label,
  value,
  icon,
}: {
  label: string;
  value: string | number;
  icon: string;
}) => {
  return (
    <div className="flex flex-row items-center justify-start gap-2">
      <Icon icon={icon} className=" text-jb-neutral-1000" fontSize={30} />
      <div className="flex flex-col">
        <Text className="text-jb-neutral-1000" variant="jb-body-small">
          {label}
        </Text>
        <Text
          className="font-semibold text-jb-neutral-1000"
          variant="jb-body-medium"
        >
          {value}
        </Text>
      </div>
    </div>
  );
};
